import type { EvidenceEntry } from "@/types/deal";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

const RESULT_STYLES: Record<string, string> = {
  pass: "bg-green-50 text-green-800 border-green-200",
  review: "bg-amber-50 text-amber-800 border-amber-200",
  flag: "bg-red-50 text-red-800 border-red-200",
};

function fmtNum(n: number | null): string {
  if (n === null) return "—";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);
}

function EvidenceCard({ entry, index }: { entry: EvidenceEntry; index: number }) {
  const resultKey = entry.result?.toLowerCase();
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Evidence {index + 1}
        </span>
        <span
          className={cn(
            "inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold tracking-wide",
            RESULT_STYLES[resultKey] ?? "bg-gray-50 text-gray-600 border-gray-200"
          )}
        >
          {entry.result?.toUpperCase()}
        </span>
      </div>

      <div>
        <p className="text-xs font-medium text-gray-500">Source Document</p>
        <p className="text-sm text-gray-900">{entry.source_document}</p>
      </div>

      {entry.source_field && (
        <div>
          <p className="text-xs font-medium text-gray-500">Source Field</p>
          <p className="text-sm text-gray-900">
            {entry.source_field}
            {entry.source_value !== null && (
              <span className="text-gray-500"> — {fmtNum(entry.source_value)}</span>
            )}
          </p>
        </div>
      )}

      {entry.calculation && (
        <div>
          <p className="text-xs font-medium text-gray-500">Calculation</p>
          <p className="text-sm text-gray-900">
            {entry.calculation}
            {entry.calculated_value !== null && (
              <span className="text-gray-500"> = {fmtNum(entry.calculated_value)}</span>
            )}
          </p>
        </div>
      )}

      {entry.threshold_used && (
        <div>
          <p className="text-xs font-medium text-gray-500">Threshold Applied</p>
          <p className="text-sm text-gray-900">
            {entry.threshold_used}
            {entry.threshold_value !== null && (
              <span className="text-gray-500"> ({fmtNum(entry.threshold_value)})</span>
            )}
          </p>
        </div>
      )}

      <div>
        <p className="text-xs font-medium text-gray-500">Confidence</p>
        <p className="text-sm text-gray-900 capitalize">{entry.confidence}</p>
      </div>
    </div>
  );
}

interface FindingEvidenceDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  findingNumber: number;
  findingTitle: string;
  evidence: EvidenceEntry[];
}

export function FindingEvidenceDrawer({
  open,
  onOpenChange,
  findingNumber,
  findingTitle,
  evidence,
}: FindingEvidenceDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            Finding {findingNumber} — {findingTitle}
          </SheetTitle>
          <SheetDescription>
            Evidence trail: how this score was derived from deal data.
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-3 px-4 pb-4">
          {evidence.length === 0 ? (
            <p className="text-sm text-gray-500">
              No evidence trail recorded for this finding yet.
            </p>
          ) : (
            evidence.map((entry, i) => (
              <EvidenceCard key={i} entry={entry} index={i} />
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default FindingEvidenceDrawer;
